import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Switch,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Divider,
  IconButton,
  Alert,
  Chip,
  TextField,
  Avatar,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Close,
  Palette,
  VolumeUp,
  Key,
  Info,
  Person,
  ArrowBack,
  Psychology,
  CheckCircle,
  OpenInNew,
  Tune,
  BugReport,
  Lightbulb,
} from '@mui/icons-material';
import FeatureControlSettings from './FeatureControlSettings';
import FeedbackDialog from './FeedbackDialog';
import { getThemePreference, saveThemePreference } from '../theme.js';
import {
  VOICE_OPTIONS,
  VOICE_LABELS,
  getVoicePreference,
  saveVoicePreference,
  testVoice,
  ensureVoicesLoaded,
  getAvailableVoices,
} from '../services/voiceService';
import {
  getProfile,
  saveProfile,
} from '../services/profileService';
import MultiProviderSettings from './MultiProviderSettings';
import TutorPersonaSettings from './TutorPersonaSettings';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const APP_VERSION = '16.7.1';

const NAV_SECTIONS = [
  {
    heading: 'Account',
    items: [
      { id: 'general',    label: 'Profile',          icon: <Person />,     description: 'Your student profile' },
      { id: 'appearance', label: 'Appearance',        icon: <Palette />,    description: 'Theme & display' },
    ],
  },
  {
    heading: 'AI & Voice',
    items: [
      { id: 'voice',   label: 'Voice & Speech',       icon: <VolumeUp />,   description: 'Text-to-speech voices' },
      { id: 'ai',      label: 'AI Providers',         icon: <Key />,        description: 'API keys & providers' },
      { id: 'persona', label: 'Ekamanam Tutor Persona', icon: <Psychology />, description: 'Tutor personality' },
    ],
  },
  {
    heading: 'Tools',
    items: [
      { id: 'features', label: 'Features',            icon: <Tune />,       description: 'Enable or disable tools' },
    ],
  },
  {
    heading: 'Support',
    items: [
      { id: 'about',   label: 'About',               icon: <Info />,       description: 'Version & credits' },
    ],
  },
];

// Flat list used by mobile tabs
const ALL_OPTIONS = NAV_SECTIONS.flatMap(s => s.items);

// ── Voice availability config ─────────────────────────────────────────────────
// Maps each voice option to the BCP-47 language prefixes to look for in the browser

const VOICE_LANG_PREFIXES = {
  indian_english_female:   { primary: ['en'],       fallback: null },
  indian_hindi_female:     { primary: ['hi'],       fallback: null },
  indian_telugu_female:    { primary: ['te'],       fallback: null },
  indian_tamil_female:     { primary: ['ta'],       fallback: null },
  indian_sanskrit_female:  { primary: ['sa'],       fallback: 'hi', fallbackLabel: 'Uses Hindi voice' },
  indian_kannada_female:   { primary: ['kn'],       fallback: null },
  indian_malayalam_female: { primary: ['ml'],       fallback: null },
  indian_bengali_female:   { primary: ['bn'],       fallback: null },
  indian_marathi_female:   { primary: ['mr'],       fallback: null },
  indian_gujarati_female:  { primary: ['gu'],       fallback: null },
  indian_punjabi_female:   { primary: ['pa'],       fallback: null },
  urdu_female:             { primary: ['ur'],       fallback: 'hi', fallbackLabel: 'Uses Hindi voice' },
};

const VOICE_DESCRIPTIONS = {
  indian_english_female:   'Clear Indian English accent',
  indian_hindi_female:     'Natural Hindi pronunciation',
  indian_telugu_female:    'Native Telugu speaker',
  indian_tamil_female:     'Native Tamil speaker',
  indian_sanskrit_female:  'Sanskrit — uses Hindi voice as fallback',
  indian_kannada_female:   'Native Kannada speaker',
  indian_malayalam_female: 'Native Malayalam speaker',
  indian_bengali_female:   'Native Bengali speaker',
  indian_marathi_female:   'Native Marathi speaker',
  indian_gujarati_female:  'Native Gujarati speaker',
  indian_punjabi_female:   'Native Punjabi speaker',
  urdu_female:             'Urdu — uses Hindi voice as fallback',
};

const VOICE_SAMPLE_TEXTS = {
  indian_english_female:   'Hello! This is how I sound — clear and natural!',
  indian_hindi_female:     'नमस्ते! यह मेरी आवाज़ है।',
  indian_telugu_female:    'హలో! ఇది నా వాయిస్.',
  indian_tamil_female:     'வணக்கம்! இது என் குரல்.',
  indian_sanskrit_female:  'नमस्ते। अहं संस्कृतम् वदामि।',
  indian_kannada_female:   'ನಮಸ್ಕಾರ! ಇದು ನನ್ನ ಧ್ವನಿ.',
  indian_malayalam_female: 'നമസ്കാരം! ഇത് എന്റെ ശബ്ദം.',
  indian_bengali_female:   'নমস্কার! এটি আমার কণ্ঠস্বর।',
  indian_marathi_female:   'नमस्कार! हा माझा आवाज आहे.',
  indian_gujarati_female:  'નમસ્તે! આ મારો અવાજ છે.',
  indian_punjabi_female:   'ਸਤਿ ਸ੍ਰੀ ਅਕਾਲ! ਇਹ ਮੇਰੀ ਆਵਾਜ਼ ਹੈ।',
  urdu_female:             'السلام علیکم! یہ میری آواز ہے۔',
};

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

function EnhancedSettingsDialog({ open, onClose, user, isDemoAccount, initialSection, onThemeChange }) {
  const theme = useTheme();
  const isMobile    = useMediaQuery(theme.breakpoints.down('md'));
  const isSmall     = useMediaQuery(theme.breakpoints.down('sm'));

  const [selectedOption,       setSelectedOption]       = useState('general');
  const [darkMode,             setDarkMode]             = useState(false);
  const [voicePreference,      setVoicePreference]      = useState(VOICE_OPTIONS.INDIAN_ENGLISH_FEMALE);
  const [studentName,          setStudentName]          = useState('');
  const [profileSaved,         setProfileSaved]         = useState(false);
  const [feedbackOpen,         setFeedbackOpen]         = useState(false);
  const [feedbackType,         setFeedbackType]         = useState('issue');
  const [availableLangPrefixes, setAvailableLangPrefixes] = useState(new Set(['en']));

  useEffect(() => {
    if (open) {
      setDarkMode(getThemePreference() === 'dark');
      setVoicePreference(getVoicePreference());
      const profile = getProfile();
      setStudentName(profile.studentName || '');
      setProfileSaved(false);
      if (initialSection) setSelectedOption(initialSection);
      // Detect which language voices the browser actually has
      ensureVoicesLoaded().then(() => {
        const voices = getAvailableVoices();
        const prefixes = new Set(voices.map(v => v.lang.split('-')[0].toLowerCase()));
        setAvailableLangPrefixes(prefixes);
      });
    }
  }, [open]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleThemeToggle = (e) => {
    const isDark = e.target.checked;
    setDarkMode(isDark);
    const mode = isDark ? 'dark' : 'light';
    saveThemePreference(mode);
    onThemeChange?.(mode);
  };

  const handleVoiceChange = (e) => {
    setVoicePreference(e.target.value);
    saveVoicePreference(e.target.value);
  };

  const handleSaveProfile = () => {
    saveProfile({ studentName });
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 3000);
  };

  // ── Section renderers ──────────────────────────────────────────────────────

  const renderGeneral = () => (
    <Box>
      <SectionHeader title="Profile" subtitle="Personalise your learning experience" />

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 3 }}>
        {user && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
            <Avatar src={user.photoURL} sx={{ width: 48, height: 48, bgcolor: 'primary.main' }}>
              {(user.displayName || user.email || '?')[0].toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" fontWeight={600}>
                {user.displayName || 'Signed in'}
              </Typography>
              <Typography variant="caption" color="text.secondary">{user.email}</Typography>
            </Box>
            <Chip label="Google" size="small" color="primary" variant="outlined" sx={{ ml: 'auto' }} />
          </Box>
        )}

        <TextField
          label="Student Name"
          value={studentName}
          onChange={(e) => setStudentName(e.target.value)}
          fullWidth
          variant="outlined"
          helperText="Used to personalise AI responses and notes"
          size={isSmall ? 'small' : 'medium'}
        />

        <Button
          variant="contained"
          onClick={handleSaveProfile}
          disabled={!studentName.trim()}
          sx={{ alignSelf: isMobile ? 'stretch' : 'flex-start', borderRadius: 2, px: 3 }}
        >
          Save Profile
        </Button>

        {profileSaved && (
          <Alert severity="success" icon={<CheckCircle />}>
            Profile saved successfully!
          </Alert>
        )}
      </Box>
    </Box>
  );

  const renderAppearance = () => (
    <Box>
      <SectionHeader title="Appearance" subtitle="Customise how Ekamanam looks" />

      <Box sx={{ mt: 3 }}>
        <SettingRow
          primary="Dark Mode"
          secondary={darkMode ? 'Easier on the eyes in low-light conditions' : 'Bright and clean for daytime study'}
        >
          <Switch checked={darkMode} onChange={handleThemeToggle} color="primary" />
        </SettingRow>

        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
          Dark mode reduces eye strain during long evening study sessions.
        </Alert>
      </Box>
    </Box>
  );

  // Returns a status Chip for a voice option based on browser availability
  const getVoiceStatusChip = (key) => {
    const cfg = VOICE_LANG_PREFIXES[key];
    if (!cfg) return null;
    const hasPrimary = cfg.primary.some(p => availableLangPrefixes.has(p));
    if (hasPrimary) {
      return <Chip label="Available" size="small" color="success" sx={{ height: 18, fontSize: '0.67rem', fontWeight: 600 }} />;
    }
    if (cfg.fallback && availableLangPrefixes.has(cfg.fallback)) {
      return <Chip label={cfg.fallbackLabel || 'Uses fallback'} size="small" color="info" sx={{ height: 18, fontSize: '0.67rem' }} />;
    }
    return <Chip label="Not installed" size="small" color="warning" sx={{ height: 18, fontSize: '0.67rem' }} />;
  };

  const renderVoice = () => (
    <Box>
      <SectionHeader title="Voice & Speech" subtitle="Choose your AI tutor's voice" />

      <Alert severity="info" sx={{ mt: 2, mb: 2.5, borderRadius: 2, py: 0.75 }}>
        <Typography variant="caption">
          <strong>Browser voices vary by device.</strong> Voices marked <strong>Not installed</strong> will fall back to English.
          Install additional voices in your OS language settings for best results.
          Chrome &amp; Edge offer the widest language support.
        </Typography>
      </Alert>

      <FormControl component="fieldset" sx={{ width: '100%' }}>
        <FormLabel sx={{ fontWeight: 600, mb: 1.5, color: 'text.primary' }}>Select Voice</FormLabel>
        <RadioGroup value={voicePreference} onChange={handleVoiceChange}>
          {Object.entries(VOICE_LABELS).map(([key, label]) => {
            const isSelected = voicePreference === key;
            const cfg = VOICE_LANG_PREFIXES[key];
            const isNativeAvailable = cfg?.primary.some(p => availableLangPrefixes.has(p));
            return (
              <Box
                key={key}
                sx={{
                  mb: 1,
                  p: 1.5,
                  border: '1.5px solid',
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  borderRadius: 2,
                  bgcolor: isSelected ? 'primary.50' : 'transparent',
                  opacity: isNativeAvailable || isSelected ? 1 : 0.72,
                  transition: 'all 0.15s',
                  '&:hover': { borderColor: 'primary.main', bgcolor: isSelected ? 'primary.50' : 'action.hover', opacity: 1 },
                }}
              >
                <FormControlLabel
                  value={key}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 0.2 }}>
                          <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>{label}</Typography>
                          {getVoiceStatusChip(key)}
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          {VOICE_DESCRIPTIONS[key] || ''}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          testVoice(key, VOICE_SAMPLE_TEXTS[key] || 'Hello!');
                        }}
                        color="primary"
                        title="Preview voice"
                      >
                        <VolumeUp fontSize="small" />
                      </IconButton>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0 }}
                />
              </Box>
            );
          })}
        </RadioGroup>
      </FormControl>
    </Box>
  );

  const renderAI = () => (
    <Box>
      <SectionHeader title="AI Providers" subtitle="Configure API keys for AI-powered features" />

      <Alert severity="info" sx={{ mt: 3, mb: 2, borderRadius: 2 }}>
        <strong>Out-of-the-box AI:</strong> Gemini 2.5 Flash (1,500 free/day), Groq Qwen3.6 (fastest), and Claude Sonnet 5 (best reasoning) — all free, no credit card. Automatic fallback between providers.
      </Alert>

      <MultiProviderSettings isDemoAccount={isDemoAccount} />
    </Box>
  );

  const renderPersona = () => (
    <Box>
      <SectionHeader title="Ekamanam Tutor Persona" subtitle="Give your tutor a personality that suits your style" />
      <Box sx={{ mt: 2 }}>
        <TutorPersonaSettings user={user} />
      </Box>
    </Box>
  );

  const renderAbout = () => (
    <Box>
      {/* App identity */}
      <Box sx={{ mt: 1, p: 2.5, bgcolor: 'primary.main', borderRadius: 3, color: 'white', textAlign: 'center' }}>
        <Typography variant="h5" fontWeight={800} letterSpacing="-0.5px">Ekamanam</Typography>
        <Typography variant="caption" sx={{ opacity: 0.85, letterSpacing: 1, textTransform: 'uppercase' }}>
          A Product of Ekamanam AI Innovations
        </Typography>
      </Box>

      <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <InfoRow label="Version" value={<Chip label={`v${APP_VERSION}`} size="small" color="primary" />} />
        <InfoRow label="Account" value={user ? user.email : <Chip label="Not signed in" size="small" />} />
      </Box>

      <Divider sx={{ my: 2.5 }} />

      {/* About Us */}
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>About Us</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.7 }}>
        Welcome to Ekamanam AI Innovations. We believe that empowering educational institutions and
        growing enterprises is key to building a thriving society. By bridging global innovation with
        local impact, we are redefining the educational landscape—cultivating a space where learners
        can lead, innovate, and thrive.
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, fontStyle: 'italic' }}>
        Meet the visionary minds driving our mission forward:
      </Typography>

      {/* Team profiles */}
      {[
        {
          name: 'Ekyom Kaur',
          tagline: 'The Visionary Student Who Sparked It All',
          bio: 'Ekyom Kaur, a ninth-grade student at Pallavi Model School, Alwal, is the initiator and original visionary behind Ekamanam. What began as a young student\'s dream to make learning more engaging and accessible has grown into a platform that aims to transform education for millions of students across the globe.\n\nAs the very first user of Ekamanam, Ekyom has been testing and using the application since its earliest version. Her keen eye for user experience and her perspective as an active student have been invaluable — she has continuously contributed ideas, tested the UI/UX, and recommended improvements that have shaped the product into what it is today. Ekyom\'s ability to envision a solution that addresses real challenges faced by students everywhere is what gave birth to Ekamanam. Her passion for making quality education accessible to every learner, regardless of where they are, continues to inspire the team and drive the mission forward.',
          photo: '/Ekyom.jpg',
          initials: 'EK',
          photoPosition: 'top center',
        },
        {
          name: 'Sarabjeet Kaur',
          tagline: 'Translating Complex Technologies into Clear Learning Pathways',
          bio: 'Sarabjeet brings a meticulous, results-oriented approach to Ekamanam\'s educational content and technical communication. Armed with a Master\'s in Computer Sciences and a rich background bridging classroom instruction with corporate technical writing at Tata Consultancy Services (TCS), she excels at translating intricate software concepts into highly accessible learning pathways.\n\nBy collaborating closely with Subject Matter Experts, Sarabjeet ensures our advanced AI and EduTech tools are supported by intuitive training materials, clear user manuals, and comprehensive technical guides. Her vital work removes the learning curve, empowering educators and learners to seamlessly adopt and master new technologies.',
          photo: '/Sarab.jpg',
          initials: 'SK',
          photoPosition: 'top center',
        },
        {
          name: 'Ramesh Raju Kanchanapally',
          tagline: 'Bridging Tradition and Technology in Modern Learning',
          bio: 'With over 24 years of strategic leadership across corporate, government, and global MNC sectors, Ramesh drives Ekamanam\'s vision of "Focused Learning." His deep operational expertise spans transformative sectors like Semiconductors, AI, IoT, and Networking, fueling his advocacy for interactive, multilingual, and learner-centric digital transformation in modern classrooms.\n\nA dedicated academician, Ramesh holds advanced degrees in Communication (MCJ) and Technology Management (MBA-TM), and is actively studying Indian Knowledge Systems (MA-IKS). He uniquely bridges traditional pedagogical wisdom with cutting-edge technology. Under his guidance, Ekamanam ensures that modern learning tools—from 3D visualizations to voice-powered AI—are built on a foundation of both technical excellence and profound educational depth.',
          photo: '/Ramesh.jpeg',
          initials: 'RK',
        },
      ].map((member) => (
        <Box
          key={member.name}
          sx={{
            mb: 2.5,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 3,
            bgcolor: 'background.paper',
          }}
        >
          {/* Photo placeholder + name */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
            <Avatar
              src={member.photo}
              alt={member.name}
              sx={{
                width: 72,
                height: 72,
                flexShrink: 0,
                border: '2px solid',
                borderColor: 'divider',
                '& img': { objectPosition: member.photoPosition || 'center' },
              }}
            >
              {member.initials}
            </Avatar>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                {member.name}
              </Typography>
              <Typography variant="caption" color="primary.main" fontWeight={500}>
                {member.tagline}
              </Typography>
            </Box>
          </Box>
          {member.bio.split('\n\n').map((para, i) => (
            <Typography key={i} variant="body2" color="text.secondary" sx={{ mb: i < member.bio.split('\n\n').length - 1 ? 1 : 0, lineHeight: 1.7 }}>
              {para}
            </Typography>
          ))}
        </Box>
      ))}

      <Divider sx={{ my: 2 }} />

      {/* Feedback */}
      <Typography variant="subtitle2" fontWeight={700} gutterBottom>Feedback</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Help us improve Ekamanam! Report issues or suggest new features.
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <Button
          size="small"
          variant="outlined"
          color="error"
          startIcon={<BugReport fontSize="small" />}
          onClick={() => { setFeedbackType('issue'); setFeedbackOpen(true); }}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Report Issue
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="success"
          startIcon={<Lightbulb fontSize="small" />}
          onClick={() => { setFeedbackType('feedback'); setFeedbackOpen(true); }}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Feedback / Feature Request
        </Button>
      </Box>

      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} user={user} initialType={feedbackType} />

      <Divider sx={{ my: 2 }} />

      {/* Links */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button
          size="small"
          variant="text"
          endIcon={<OpenInNew fontSize="small" />}
          href="https://www.ekamanam.com"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ alignSelf: 'flex-start', textTransform: 'none', pl: 0 }}
        >
          www.ekamanam.com
        </Button>
      </Box>

      <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 2.5 }}>
        © {new Date().getFullYear()} Ekamanam AI Innovations. All rights reserved.
      </Typography>
    </Box>
  );

  const renderContent = () => {
    switch (selectedOption) {
      case 'general':    return renderGeneral();
      case 'appearance': return renderAppearance();
      case 'voice':      return renderVoice();
      case 'ai':         return renderAI();
      case 'persona':    return renderPersona();
      case 'features':   return <Box><FeatureControlSettings user={user} /></Box>;
      case 'about':      return renderAbout();
      default:           return null;
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          height: isMobile ? '100vh' : '88vh',
          maxHeight: isMobile ? '100vh' : '88vh',
          m: isMobile ? 0 : 2,
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
        },
      }}
    >
      <Box sx={{ display: 'flex', height: '100%', flexDirection: 'column' }}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.5,
          px: 2.5, py: 1.75,
          borderBottom: '1px solid', borderColor: 'divider',
          bgcolor: 'background.paper', flexShrink: 0,
        }}>
          {isMobile && (
            <IconButton onClick={onClose} size="small" edge="start">
              <ArrowBack />
            </IconButton>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>Settings</Typography>
            {!isMobile && user && (
              <Typography variant="caption" color="text.secondary">{user.email}</Typography>
            )}
          </Box>
          {!isMobile && (
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          )}
        </Box>

        {/* ── Mobile: top tab bar ─────────────────────────────────────────── */}
        {isMobile && (
          <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', overflowX: 'auto', flexShrink: 0 }}>
            <Tabs
              value={selectedOption}
              onChange={(_, v) => setSelectedOption(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ minHeight: 48, '& .MuiTab-root': { minHeight: 48, fontSize: '0.78rem', minWidth: 80, px: 1.5 } }}
            >
              {ALL_OPTIONS.map(o => (
                <Tab key={o.id} value={o.id} icon={React.cloneElement(o.icon, { fontSize: 'small' })} iconPosition="start" label={o.label} />
              ))}
            </Tabs>
          </Box>
        )}

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

          {/* Desktop left nav */}
          {!isMobile && (
            <Box sx={{
              width: 210, flexShrink: 0,
              borderRight: '1px solid', borderColor: 'divider',
              display: 'flex', flexDirection: 'column',
              overflow: 'auto', bgcolor: 'background.default',
            }}>
              {NAV_SECTIONS.map((section, si) => (
                <Box key={si}>
                  {si > 0 && <Divider />}
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    color="text.disabled"
                    sx={{ px: 2, pt: 2, pb: 0.5, display: 'block', letterSpacing: '0.06em', textTransform: 'uppercase' }}
                  >
                    {section.heading}
                  </Typography>
                  <List dense disablePadding>
                    {section.items.map(item => {
                      const active = selectedOption === item.id;
                      return (
                        <ListItem key={item.id} disablePadding>
                          <ListItemButton
                            selected={active}
                            onClick={() => setSelectedOption(item.id)}
                            sx={{
                              mx: 1, mb: 0.25, borderRadius: 1.5,
                              '&.Mui-selected': {
                                bgcolor: 'primary.main',
                                color: 'primary.contrastText',
                                '&:hover': { bgcolor: 'primary.dark' },
                                '& .MuiListItemIcon-root': { color: 'primary.contrastText' },
                                '& .MuiTypography-root': { color: 'primary.contrastText' },
                              },
                              '&:not(.Mui-selected):hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36, color: active ? 'inherit' : 'action.active' }}>
                              {React.cloneElement(item.icon, { fontSize: 'small' })}
                            </ListItemIcon>
                            <ListItemText
                              primary={item.label}
                              primaryTypographyProps={{ variant: 'body2', fontWeight: active ? 600 : 500 }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              ))}
            </Box>
          )}

          {/* Content panel */}
          <DialogContent sx={{
            flex: 1, overflow: 'auto', p: isMobile ? (isSmall ? 2 : 2.5) : 3.5,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'divider', borderRadius: 3 },
          }}>
            {renderContent()}
          </DialogContent>
        </Box>
      </Box>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Small reusable layout helpers (defined inside file to avoid extra imports)
// ─────────────────────────────────────────────────────────────────────────────

function SectionHeader({ title, subtitle }) {
  return (
    <Box sx={{ borderBottom: '2px solid', borderColor: 'primary.main', pb: 1.5, mb: 0 }}>
      <Typography variant="h6" fontWeight={700}>{title}</Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>{subtitle}</Typography>
      )}
    </Box>
  );
}

function SettingRow({ primary, secondary, children }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2,
      '&:hover': { bgcolor: 'action.hover' }, transition: 'background 0.15s',
    }}>
      <Box sx={{ flex: 1, pr: 2 }}>
        <Typography variant="body1" fontWeight={500}>{primary}</Typography>
        {secondary && <Typography variant="caption" color="text.secondary">{secondary}</Typography>}
      </Box>
      {children}
    </Box>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.75 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {typeof value === 'string'
        ? <Typography variant="body2" fontWeight={500}>{value}</Typography>
        : value}
    </Box>
  );
}

export default EnhancedSettingsDialog;
